import { css } from 'lit';
import { CSSResultArray, LitElement, TemplateResult, html, nothing } from 'lit';
import bind from 'bind-decorator';
import { customElement, property, query, state } from 'lit/decorators.js';
import { connect } from '@/utils/connect';
import { ifDefined } from 'lit/directives/if-defined.js';
import { Entries } from '@typings/shared-types';
import ConfigCARSx from '../../config/ConfigCARSx';
import './notification-banner';
import bbuttonbaseScss from '../css/lib/bbuttonbase.scss';
import sharedScss from '../css/shared.scss';
import resetScss from '../css/lib/reset.scss';
import store, { ResponsiveIBrowser, RootState } from '../../redux/redux-store';
import { AppSection, Breakpoint, MobileBreakpoint } from '../../constants';
import containsTargetPenetrate from '../../utils/contains-penetrate';
import { navigate } from '../../redux/redux-routing';
import { selectShowReadOnly } from '../../redux/redux-user';
import { userHasPermission } from '../../redux/user-permissions';
import './nav-menu-item';
import { UserPermissions } from '../../../typings/api';
import { PageConfig } from '../../config/ConfigCARSx';
import { ConfigUserManagement } from '../../config/ConfigLogin';
import { selectNumberOfWorkZoneNotifications } from '../../redux/rem/rem-selectors';
import pageNavScss from './page-nav.scss';
import './app-icon';
import './app-bubble';
import './alerts-panel';
import utilityClassesCss from '../css/utilityClasses.css';

/**
 * @returns {boolean} whether the menu hide operation should be blocked
 */
function menuHideBlocker(): boolean {
	return window.innerWidth < MobileBreakpoint;
}

@customElement('page-nav')
export default class extends connect(store)(LitElement) {
	@property({ type: String, reflect: true }) public page?: AppSection;

	@property({ type: String, reflect: true }) public view?: string;

	@state() private mediaType?: ResponsiveIBrowser['mediaType'];

	@state() private showReadOnly?: boolean;

	@query('#menu-container') private menuContainer?: HTMLDetailsElement;

	@state() private username = '';

	@state() private roles: string[] = [];

	@state() private numberOfWorkZoneRequestsToBeReviewed = 0;

	public static override get styles(): CSSResultArray {
		return [resetScss, bbuttonbaseScss, sharedScss, pageNavScss, utilityClassesCss];
	}

	closeMobile(): void {
		if (this.menuContainer && this.mediaType === Breakpoint.mobile) {
			this.menuContainer.open = false;
		}
	}

	notificationCountForPage(appSection: AppSection): number {
		if (AppSection.WZRT === appSection) {
			return this.numberOfWorkZoneRequestsToBeReviewed;
		}

		return 0;
	}

	@bind
	private renderMenuItemToTemplate([appSection, item]: [AppSection, PageConfig]) {
		if (
			(item.permission !== undefined && userHasPermission(item.permission) === false) ||
			(item.disallowedPermission !== undefined && userHasPermission(item.disallowedPermission)) ||
			item.active === false ||
			item.renderNavbar === false
		) {
			return nothing;
		}
		return html`<li>
			<nav-menu-item
				?active=${this.page === item.pageID}
				.subpages=${item.subpages ?? []}
				.icon=${item.icon ?? ''}
				.iconWidth=${item.iconWidth ?? 24}
				.iconHeight=${item.iconHeight ?? 24}
				.notificationCount=${this.notificationCountForPage(appSection)}
				href=${ifDefined(item.route)}
				label=${item.pageTitle}
				@linkClick=${this.onLinkClicked}
				@closeNav=${this.closeMobile}
			></nav-menu-item>
		</li>`;
	}

	private onLinkClicked(e: CustomEvent<string>): void {
		void store.dispatch(navigate(e.detail));
	}

	@bind
	private renderSignout() {
		const showManageUsersPage =
			ConfigUserManagement.showManageUsersPage === true &&
			userHasPermission(UserPermissions.USER_MANAGEMENT_ACCESS) === true;
		const subpages: Partial<PageConfig>[] = [
			{
				pageTitle: this.roles.join(', '),
			},
		];
		if (showManageUsersPage) {
			subpages.push({
				pageID: 'user-groups',
				pageTitle: ConfigCARSx.Pages[AppSection.USER_GROUPS].pageTitle,
				route: ConfigCARSx.Pages[AppSection.USER_GROUPS].route,
			});
		}
		subpages.push({
			pageID: 'signout',
			pageTitle: 'Sign Out',
			route: `/logout`,
		});
		return html`<div class="h-100 flex align-items-center">
			<nav-menu-item
				class="signout-menu"
				?active=${false}
				.subpages=${subpages}
				icon="img/icon-profile-fill-solid.svg"
				label=${this.username}
				@closeNav=${this.closeMobile}
			></nav-menu-item>
			${this.showReadOnly
				? html`<span class="pill pill-caution"
						><app-icon
							href="img/sprites.svg#read-icon"
							height="20"
							style="stroke: rgb(var(--brand-status-caution))"
						></app-icon
						>View Only</span
					>`
				: nothing}
		</div> `;
	}

	public override render(): TemplateResult {
		return html`
			<header id="topbar">
				<h1 id="logo-link">
					<a href="${ConfigCARSx.Logo.href}" title="Home"
						><img src="${ConfigCARSx.Logo.icon}" alt="${ConfigCARSx.Logo.alt}" height="64"
					/></a>
				</h1>
				<h2 id="page-title">${this.page ? ConfigCARSx.Pages[this.page].pageTitle : nothing}</h2>
				${this.showReadOnly ? html`<span id="read-only-tag-mobile">View Only</span>` : nothing}
				<details id="menu-container" ?open=${this.mediaType === Breakpoint.desktop}>
					<summary><span>Menu</span></summary>
					<nav class="details-content">
						<ul class="left-links">
							${(Object.entries(ConfigCARSx.Pages) as Entries<typeof ConfigCARSx.Pages>).map(
								this.renderMenuItemToTemplate,
							)}
						</ul>
						<ul class="right-links">
							<li>${this.renderSignout()}</li>
							${ConfigCARSx.showAlerts ? html`<li><alerts-panel></alerts-panel></li>` : nothing}
						</ul>
					</nav>
				</details>
			</header>
			<notification-banner></notification-banner>
		`;
	}

	public override stateChanged(state: RootState): void {
		this.page = state.routing.page ?? undefined;
		this.view = state.routing.view ?? undefined;
		this.mediaType = state.responsive?.mediaType;
		this.showReadOnly = selectShowReadOnly(state);
		this.username = state.user?.authority?.name ?? '';
		this.roles =
			(state.user?.authority?.userGroups?.length
				? state.user?.authority?.userGroups
				: state.user?.authority?.roles) ?? [];

		this.numberOfWorkZoneRequestsToBeReviewed = selectNumberOfWorkZoneNotifications(state);
	}

	@bind
	private onGlobalMouseup(e: MouseEvent): void {
		if (
			menuHideBlocker() &&
			this.menuContainer?.open === true &&
			!containsTargetPenetrate(this.menuContainer, e)
		) {
			this.menuContainer.open = false;
			this.menuContainer.blur();
		}
	}

	public override connectedCallback(): void {
		window.addEventListener('mouseup', this.onGlobalMouseup);
		super.connectedCallback();
	}

	public override disconnectedCallback(): void {
		window.removeEventListener('mouseup', this.onGlobalMouseup);
		super.disconnectedCallback();
	}
}
